import { partial } from "lodash";
import { useState } from "react";

import { AddToPacksButton } from "../../../../../components/buttons/AddToPacksButton/AddToPacksButton";
import { DuplicateLinkButton } from "../../../../../components/buttons/linkButtons";
import { OverviewHeader } from "../../../../../components/sectionHeader";
import { RolePermissions } from "../../../../../enums";
import { type FlowMap, type SelectionContext } from "../types";
import { CreateFlowButton } from "./CreateFlowButton/CreateFlowButton";
import { DeleteFlowButton } from "./DeleteFlowButton/DeleteFlowButton";

import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import AddAssetsToPackModal from "../../../../../components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import Restricted from "../../../../../features/Application/Restricted";
import TagsManageContainer from "../containers/TagsManageContainer";
import FlowPeopleAssignment from "./FlowPeopleAssignment/FlowPeopleAssignment";
import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";

export interface FlowOverviewHeaderProps {
  selectedIds: Array<number>;
  selectionCtx: SelectionContext;
  onDuplicateFlows: (selectedIds: number[]) => () => void;
  onToggleVisibility: (selectedIds: number[], hidden: boolean) => () => void;
  flowDeletionHandler: (selectedIds: number[]) => () => void;
  setSelectedIds: (ids: number[]) => void;
  clearSelection: () => void;
  flowMap: FlowMap;
}

export function FlowsOverviewHeader(props: FlowOverviewHeaderProps) {
  const { selectedIds, selectionCtx, flowMap } = props;
  const [packsModalShown, setPacksModalShown] = useState<boolean>(false);
  const purchasedSelected = selectionCtx.purchased > 0;
  const draftSelected = selectionCtx.drafts > 0;
  const undeletableSelected = selectionCtx.undeletable > 0;
  const flowMigrationEnabled = useFeatureFlag(FeatureFlags.FlowVersioningMigration);

  const onModalClose = () => {
    setPacksModalShown(false);
    props.clearSelection();
  };

  const renderBatchButtons = () => {
    const hiddenAndShownSelected = selectionCtx.hidden > 0 && selectionCtx.shown > 0;

    return (
      <>
        <ToggleVisibilityButton
          hidden={selectionCtx.hidden > 0}
          onClick={props.onToggleVisibility(selectedIds, selectionCtx.hidden > 0)}
          isDisabled={hiddenAndShownSelected || selectionCtx.unpublished > 0}
        />
        <Restricted permissions={[RolePermissions.PacksManage, RolePermissions.FlowsCreate]}>
          <AddToPacksButton
            onClick={partial(setPacksModalShown, true)}
            isDisabled={purchasedSelected || draftSelected}
          />
        </Restricted>
        <DuplicateLinkButton onClick={props.onDuplicateFlows(selectedIds)} isDisabled={flowMigrationEnabled} />
        <DeleteFlowButton
          undeletableSelected={undeletableSelected}
          purchasedSelected={purchasedSelected}
          onClick={props.flowDeletionHandler(selectedIds)}
        />
        <FlowPeopleAssignment
          flowMap={flowMap}
          setSelectedIds={props.setSelectedIds}
          selectedIds={selectedIds}
          selectionCtx={selectionCtx}
        />
        <TagsManageContainer
          entity="Flows"
          isDisabled={purchasedSelected}
          selectedIds={selectedIds}
          onComplete={props.clearSelection}
          onClose={props.clearSelection}
        />
        <AddAssetsToPackModal
          showModal={packsModalShown}
          onClose={onModalClose}
          onComplete={props.clearSelection}
          selectedItemIds={selectedIds}
          contentType="Flow"
        />
      </>
    );
  };

  return (
    <OverviewHeader selectedItemsAmount={selectedIds.length} itemName="Flow" title="Flows">
      {selectedIds.some(Boolean) ? renderBatchButtons() : <CreateFlowButton />}
    </OverviewHeader>
  );
}

export default FlowsOverviewHeader;
